<template>
  <main-container class="model-predict-container" title="モデル適用" :subTitle="modelName">
    <div slot="main-content">
      <form-container>
        <form-item-container label="適用データ選択" :tip="dsLabelTip" annotation="* 500MBまで"
                             v-if="modelInfo && modelInfo.categoryId !== 'TSA'">
          <field-container :class="{ 'has-error': dsValidateError }">
            <field-upload-ds method="predict"
                             :url="`${this.$session.get('API_URL')}ds/put`"
                             v-model="ds"
                             :modelId="modelId"
                             @added="onUploaded"
                             @removed="onRemoved"
                             style="width: 100%;"></field-upload-ds>
            <span class="error-message"><strong>適用データを選択してください</strong></span>
          </field-container>
        </form-item-container>

        <form-item-container label="適用データの削除" annotation="* モデル適用に使用したデータをサーバ上から削除します"
                             v-if="modelInfo && modelInfo.categoryId !== 'TSA'">
          <field-container>
            <field-radio-group v-model="deleteUploadFile" name="deleteUploadFile"
                               :options="[{label: '削除しない', value: false}, {label: '削除する', value: true}]"></field-radio-group>
          </field-container>
        </form-item-container>

        <form-item-container label="出力する結果" v-if="modelInfo && isBinaryClassificationModel()">
          <field-container>
            <field-radio-group v-model="returnScore" name="returnScore" :display-inline="false"
                               :options="[{label: '予測確率のみ', value: null}, {label: '予測確率と予測スコア（1の予測確率が高いほど高スコア）', value: 'return_score'}, {label: '予測確率と予測スコア（1の予測確率が低いほど高スコア）', value: 'return_score_reverse'}]"></field-radio-group>
          </field-container>
        </form-item-container>

        <form-item-container label="取得するデータ"
                             v-if="modelInfo && modelInfo.categoryId !== 'TSA' && modelInfo.categoryId !== 'UNS'">
          <field-container>
            <field-radio-group v-model="returnInput" name="returnInput"
                               :options="[{label: '結果のみ', value: '0'}, {label: '元のデータと結果の両方', value: '1'}]"></field-radio-group>
          </field-container>
        </form-item-container>

        <form-item-container label="予測期間" v-if="modelInfo && modelInfo.categoryId === 'TSA'">
          <field-container :class="{ 'has-error': forecastPeriodValidateError }">
            <div>
              <el-date-picker v-model="forecastPeriodStartDate" type="date" placeholder="開始日" format="yyyy/MM/dd"
                              value-format="yyyy/MM/dd" :picker-options="forecastPickerOptions"
                              @change="validateForecastPeriod"></el-date-picker>
              <span style="margin: 0 16px">～</span>
              <el-date-picker v-model="forecastPeriodEndDate" type="date" placeholder="終了日" format="yyyy/MM/dd"
                              value-format="yyyy/MM/dd" :picker-options="forecastPickerOptions"
                              @change="validateForecastPeriod"></el-date-picker>
            </div>
            <span class="error-message"><strong>{{forecastPeriodValidateError}}</strong></span>
          </field-container>
        </form-item-container>
      </form-container>

      <predict-result-dialog v-if="predictResult != null && visiblePredictResult"
                             :visible="visiblePredictResult"
                             :modelName="modelInfo.modelName"
                             :uploadFileName="uploadFileName"
                             :predictResult="predictResult"
                             @close="visiblePredictResult=false">
      </predict-result-dialog>
    </div>

    <template slot="main-footer">
      <el-button class="large" type="default"
                 @click="changeLocation('model-detail', {modelId: modelId, modelName: modelName}, false, true)">戻る
      </el-button>
      <el-button class="large" type="primary" @click="onClickPredictResultList">適用結果一覧</el-button>
      <el-button class="large" type="info" @click="visiblePredictResult=true" v-show="predictResult != null">結果を表示
      </el-button>
      <el-button class="large" type="primary" @click="onClickPredict">モデルを適用</el-button>
    </template>
  </main-container>
</template>

<script lang="js">
import BaseView from '../base'
import PredictResultDialogCmp from '../../components/model/predict/dialogs/predict-result.vue'
import WebApiClient from '../../api/web'
import { ModelDetailInfo} from '../../defines/model/model'
import { Code } from '../../api/base'
import CommonUtil from '../../utils/common'
import DateUtil from '../../utils/date'
import { Category } from '../../defines/const'

export default {
  name: 'ModelPredictView',
  mixins: [BaseView],
  components: {
    'predict-result-dialog': PredictResultDialogCmp
  },
  props: {
    modelId: '',
    modelName: ''
  },
  computed: {
    dsLabelTip () {
      return `モデルに投入し予測値を得るためのデータです。
                         ファイル形式は、CSVファイルにのみ対応しています。
                         "key"または"id"という項目名を付けると、データを識別する項目として選択されます。
                         なければ、各データに連番を付与します。

                         画像を用いるモデルの場合、同時にアップロードする画像ファイルの中のファイル名を指定してください。
                         なお、項目名は「image」としてください。`
    },
    forecastPickerOptions () {
      const me = this
      return {
        disabledDate (date) {
          return (CommonUtil.isNotNull(me.forecastPeriodMinDate) && me.forecastPeriodMinDate.isAfter(date))
        }
      }
    }
  },
  data () {
    return {
      modelInfo: ModelDetailInfo,
      ds: {
        dsName: null,
        uploadFiles: []
      },
      returnInput: '0',
      forecastPeriodMinDate: null,
      forecastPeriodStartDate: '',
      forecastPeriodEndDate: '',
      uploadFileName: '',
      dsValidateError: false,
      forecastPeriodValidateError: '',
      predictResult: null,
      visiblePredictResult: false,
      deleteUploadFile: false,
      returnScore: null
    }
  },
  created () {
    WebApiClient.getModel(this, this.modelId).then(response => {
      if (response.data.code === Code.SUCCESS) {
        this.modelInfo = response.data.result
        this.modelName = this.modelInfo.modelName
        if (this.modelInfo.categoryId === Category.TSA) {
          const trainIntervalEndDate = DateUtil.parse(this.modelInfo.trainInterval[1], 'YYYY-MM-DD HH:mm:ss')
          this.forecastPeriodMinDate = trainIntervalEndDate.add(1, 'day')
          this.forecastPeriodStartDate = DateUtil.format(this.forecastPeriodMinDate, 'YYYY/MM/DD')
        }
        this.$forceUpdate()
      } else {
        this.changeLocation('model-list', null, false, true)
      }
    })
  },
  methods: {
    onUploaded (ds) {
      this.uploadFileName = ds.uploadFiles.slice(-1)[0].originalFileName
      this.dsValidateError = false
    },
    onRemoved () {
      this.dsValidateError = true
    },
    buildModelPredictParam () {
      if (this.modelInfo.categoryId !== Category.TSA) {
        let param = {
          dsName: this.ds.dsName,
          returnInput: Number(this.returnInput),
          deleteUploadFile: this.deleteUploadFile
        }

        // update param when Binary classification.
        if (this.isBinaryClassificationModel()) {
          param.returnScore = this.returnScore
        }

        return param
      } else {
        return {
          dsName: this.ds.dsName,
          forecastPeriod: {
            startDate: this.forecastPeriodStartDate,
            endDate: this.forecastPeriodEndDate
          }
        }
      }
    },
    isValid () {
      if (this.modelInfo.categoryId !== Category.TSA) {
        this.dsValidateError = (CommonUtil.isEmpty(this.ds.dsName))
      } else {
        this.validateForecastPeriod()
      }

      return new Promise((resolve) => {
        this.$validator.validateAll().then((result) => {
          resolve(!this.dsValidateError && CommonUtil.isEmpty(this.forecastPeriodValidateError) && result)
        })
      })
    },
    validateForecastPeriod () {
      this.forecastPeriodValidateError = ''
      if (CommonUtil.isEmpty(this.forecastPeriodStartDate)) {
        this.forecastPeriodValidateError = '予測期間の開始日は必須です'
      } else if (CommonUtil.isEmpty(this.forecastPeriodEndDate)) {
        this.forecastPeriodValidateError = '予測期間の終了日は必須です'
      } else {
        const startDate = DateUtil.parse(this.forecastPeriodStartDate, 'YYYY/MM/DD')
        const endDate = DateUtil.parse(this.forecastPeriodEndDate, 'YYYY/MM/DD')
        if (!startDate.isValid()) {
          this.forecastPeriodValidateError = '予測期間の開始日はYYYY/MM/DDのフォーマットで入力してください'
        } else if (!endDate.isValid()) {
          this.forecastPeriodValidateError = '予測期間の終了日はYYYY/MM/DDのフォーマットで入力してください'
        } else if (!startDate.isSameOrBefore(endDate)) {
          this.forecastPeriodValidateError = '予測期間の開始日が終了日よりも過去の日付になるよう指定してください'
        }
      }

      this.$forceUpdate()
    },
    onClickPredict () {
      this.isValid().then(valid => {
        if (!valid) return

        this.predictResult = null
        this.$forceUpdate()

        WebApiClient.postModelPredict(this, this.modelId, this.buildModelPredictParam()).then((response) => {
          if (response.data.code === Code.SUCCESS) {
            this.predictResult = response.data.result
            this.visiblePredictResult = true
            this.$forceUpdate()
          } else if (response.data.code === Code.TIMEOUT) {
            this.changeLocation('model-predict-result-list', {
              modelId: this.modelId,
              modelName: this.modelName
            }, false, true)
          }
        })
      })
    },
    onClickPredictResultList () {
      this.changeLocation('model-predict-result-list', {
        modelId: this.modelId,
        modelName: this.modelName
      }, false, true)
    },
    isBinaryClassificationModel () {
      return this.modelInfo.categoryId === Category.CLF && this.modelInfo.yClasses.length === 2
    }
  }
}

</script>

<style scoped>
  .model-predict-container >>> .main-content {
    padding: 24px;
  }
</style>



// WEBPACK FOOTER //
// src/views/model/predict.vue